import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FingerprintAIO } from '@ionic-native/fingerprint-aio/ngx';
import { NavController } from '@ionic/angular';
import { NavigationOptions } from '@ionic/angular/providers/nav-controller';
import { AlertButton } from '@ionic/core';
import { EMPTY, Observable, ReplaySubject, Subject, from, fromEvent, of, throwError } from 'rxjs';
import { catchError, filter, map, mapTo, mergeMap, mergeMapTo, retryWhen, take, tap } from 'rxjs/operators';
import { ErrorService } from '../../anakin/features/connexion/services/error.service';
import { SurvicateService } from '../../anakin/features/shared/services/survicate.service';
import { ArrayHelper } from '../../helpers/arrayHelper';
import { ContactHelper } from '../../helpers/contactHelper';
import { DateHelper } from '../../helpers/dateHelper';
import { IdHelper } from '../../helpers/idHelper';
import { ObjectHelper } from '../../helpers/objectHelper';
import { StoreDocumentHelper } from '../../helpers/storeDocumentHelper';
import { StringHelper } from '../../helpers/stringHelper';
import { EPrefix } from '../../../../../apps/idl/src/model/EPrefix';
import { ESuffix } from '../../../../../apps/idl/src/model/ESuffix';
import { IHttpOptions } from '../../../../../apps/idl/src/model/IHttpOptions';
import { IIndexedArray } from '../../../../../apps/idl/src/model/IIndexedArray';
import { EApplicationEventType } from '../../model/application/EApplicationEventType';
import { IUser } from '../../model/application/IUser';
import { UserData } from '../../model/application/UserData';
import { ApplicationService } from './application.service';
import { FlagService } from './flag.service';
import { ShowMessageParamsPopup } from './interfaces/ShowMessageParamsPopup';
import { NetworkService } from './network.service';
import { PlatformService } from './platform.service';
import { Store } from '../store.service';
import { UiMessageService } from './uiMessage.service';
import { WorkspaceService } from './workspace.service';
import { AuthenticatedRequestOptionBuilder } from 'apps/idl/src/model/api/authenticated-request-option-builder';
import { OsappError } from 'apps/idl/src/model/errors/OsappError';
import { ISecurityEvent, IAuthStatus, EConfigFlag, IDataSource, ConfigData, EStoreFlag, EDatabaseRole, ESyncType, ICredentialsDocument, Credentials, ISecuritySettingsDocument, ICredentials, ISecuritySettings, IStoreDataResponse, ISession, EStoreType, IAuthenticatorParams, EUnlockMode, IWorkspaceInfo } from 'apps/idl/src/model';
import { OsappApiHelper } from 'apps/idl/src/helpers/osapp-api.helper';
import { PinService } from 'apps/idl/src/modules/security/pin/services/pin.service';
import { ELogActionId } from '../../modules/logger/models/ELogActionId';
import { LoggerService } from '../../modules/logger/services/logger.service';
import { ESignupErrorCode } from '../../modules/signup/model/ESignupErrorCode';

@Injectable({ providedIn: "root" })
export class SecurityService {

	//#region FIELDS

	private static readonly C_APPLICATION_CREDENTIALS_STORAGE_DOC_ID = "app_storage_credentials";
	private static readonly C_APPLICATION_SECURITY_SETTINGS_STORAGE_DOC_ID = "app_storage_security_settings";
	private static readonly C_LOG_ID = "SEC.S";

	private readonly moAuthenticatedSubject = new ReplaySubject<boolean>();
	/** Subject pour l'envoi d'événement. */
	private readonly moEventSubject = new Subject<ISecurityEvent>();
	/** Sujet de notification du statut de l'authentification. */
	private moAuthStatusSubject = new ReplaySubject<IAuthStatus>(1);

	private C_DATABASE_ID_APP_STORAGE: string;
	private get touchScreenEventName(): string {
		return this.isvcPlatform.isMobile ? "touchstart" : "mousemove";
	};

	//#endregion

	//#region PROPERTIES

	//Observable permettant d'afficher les erreurs sur la page de connexion
	public errorConnexion$: Observable<string | null>;

	public isDomListening: boolean;

	public get authenticationStatus$(): Observable<IAuthStatus> {
		return this.isvcFlag.waitForFlag(EConfigFlag.ConfigReady, true)
			.pipe(
				mergeMap(() => this.moAuthStatusSubject.asObservable()),
				take(1)
			);
	}

	public get anakinAuthenticationStatus$(): Observable<IAuthStatus> {
		return this.isvcFlag.waitForFlag(EConfigFlag.ConfigReady, true)
			.pipe(
				mergeMap(() => this.moAuthStatusSubject.asObservable()),
			);
	}

	private get credentialsDataSource(): IDataSource {
		return {
			databaseId: this.C_DATABASE_ID_APP_STORAGE,
			viewParams: {
				key: SecurityService.C_APPLICATION_CREDENTIALS_STORAGE_DOC_ID,
				include_docs: true
			}
		} as IDataSource;
	}

	private get securitySettingsDataSource(): IDataSource {
		return {
			databaseId: this.C_DATABASE_ID_APP_STORAGE,
			viewParams: {
				key: SecurityService.C_APPLICATION_SECURITY_SETTINGS_STORAGE_DOC_ID,
				include_docs: true
			}
		} as IDataSource;
	}

	private get securityApiPath(): string {
		return `${ConfigData.environment.cloud_url}${ConfigData.environment.cloud_api_suffix}apps/${ConfigData.appInfo.appId}/security`;
	}

	private msUserId: string;
	public get userId(): string { return this.msUserId; }

	//#endregion

	//#region METHODS

	constructor(
		/** Service de gestion des requêtes en base de données. */
		private isvcStore: Store,
		private isvcNetwork: NetworkService,
		public ioHttpClient: HttpClient,
		/** Service de gestion des popups et toasts. */
		private isvcUiMessage: UiMessageService,
		private ioFingerprintAIO: FingerprintAIO,
		private ioNavController: NavController,
		private isvcPlatform: PlatformService,
		private readonly isvcLogger: LoggerService,
		private readonly isvcWorkspace: WorkspaceService,
		private isvcFlag: FlagService,
		private readonly isvcPin: PinService,
		private svcError: ErrorService,
		private svcSurvicate: SurvicateService
	) {

		isvcFlag.waitForFlag(EStoreFlag.DBLocalInitialized, true)
			.pipe(tap(_ => this.C_DATABASE_ID_APP_STORAGE = ArrayHelper.getFirstElement(this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.applicationStorage))))
			.subscribe();

		isvcFlag.waitForFlag(EConfigFlag.ConfigReady, true)
			.pipe(tap(_ => this.raiseStartHeartbeatEvent()))
			.subscribe();

		this.moAuthenticatedSubject.asObservable()
			.pipe(
				mergeMap((pbResult: boolean) => pbResult ? (UserData.current.versionAppErgo === "2" ? of(true) : this.isvcWorkspace.selectCurrentWorkspace().pipe(mapTo(pbResult))) : of(pbResult)),
				tap(
					(pbResult: boolean) => {
						this.raiseSecurityEvent({
							type: EApplicationEventType.SecurityEvent,
							createDate: new Date(),
							data: { isAuthenticated: pbResult }
						} as ISecurityEvent);
					},
					poError => console.error(`${SecurityService.C_LOG_ID}'moAuthenticatedSubject' a levé une erreur :`, poError)
				)
			)
			.subscribe();
	}

	/** Retourne le login du compte invité. */
	public getLoginInvite(): string {
		return `invite.${ConfigData.appInfo.appId}@idelizy.fr`;
	}

	/** Initialise le statut d'authentification de l'utilisateur. */
	public initUserAuthStatus(): Observable<boolean> {
		return this.getUserCredentials()
			.pipe(
				mergeMap((poCredentials: Credentials) => poCredentials.isValidToken() && !ConfigData.appInfo.unlockRequired ?
					this.unlockApp(poCredentials) : of(this.setAuthenticationStatus(false)).pipe(mapTo(false))
				),
				retryWhen((poError$: Observable<any>) => poError$.pipe(mergeMap(poError => poError.status === 401 ? EMPTY : throwError(poError))))
			);
	}

	/** Initialise l'application pour un accès invité.
	 * @param paUrlParams Tableau des paramètres passés par l'url.
	 * @param poConfigData Tableau des données de configuration de l'application.
	 */
	public initGuest(psWorkspaceId: string): Observable<boolean> {
		const lsWorkSpace = `${ConfigData.appInfo.appId}_core_ws_${psWorkspaceId}${ESuffix.workspace}`;
		return this.authenticateUser(this.getLoginInvite(), ConfigData.authentication.appCredentials.password)
			.pipe(
				tap(() => ConfigData.databases.push({ id: lsWorkSpace, syncType: ESyncType.remote, roles: [EDatabaseRole.workspace, EDatabaseRole.contacts] })),
				mergeMap(_ => this.isvcStore.initDynamicDatabases()),
				mapTo(true)
			);
	}

	/** Vérifie si la date d'expiration est dépassée.
	 * @return `true` si la date est expirée, sinon `false`.
	 */
	public checkTokenExpiration(): Observable<boolean> {
		return this.getUserCredentials()
			.pipe(
				map((poCredentials?: Credentials) =>
					(poCredentials?.tokenExpirationDate && (DateHelper.compareTwoDates(new Date(poCredentials.tokenExpirationDate), new Date()) <= 0)) ?? true
				)
			);
	}

	/** Récupère les Credentials sauvgardées dans la base de données d'application.
	 * @returns Observable de Credentials si les credentials n'ont jamais été sauvgardés on obtient "null".
	 */
	public getUserCredentials(): Observable<Credentials> {
		return this.isvcStore.getLocal(SecurityService.C_APPLICATION_CREDENTIALS_STORAGE_DOC_ID, this.C_DATABASE_ID_APP_STORAGE)
			.pipe(
				// TODO TB: RETROCOMPAT com 3.01.292
				mergeMap((poResult: ICredentialsDocument) => {
					if (!poResult) {
						return this.isvcStore.getOne(this.credentialsDataSource, false).pipe(tap((poOldResult: ICredentialsDocument) => {
							if (poOldResult)
								poOldResult._rev = undefined;
						}));
					}

					return of(poResult);
				}),
				tap(_ => { }, poError => console.error(`${SecurityService.C_LOG_ID}Erreur récupération credentials utilisateur :`, poError)),
				map((poResult: ICredentialsDocument) => poResult ? Object.assign(new Credentials(), poResult) : new Credentials())
			);
	}

	/** Récupère les paramètres de sécurité. */
	public getSavedSecuritySettings(): Observable<ISecuritySettingsDocument> {
		return this.isvcStore.getOne<ISecuritySettingsDocument>(this.securitySettingsDataSource, false)
			.pipe(tap(_ => { }, poError => console.error(`${SecurityService.C_LOG_ID}Erreur récupération paramètres de sécurité :`, poError)));
	}

	private raiseSecurityEvent(poEvent: ISecurityEvent): void {
		this.moEventSubject.next(poEvent);
	}

	/** Sauvegarde les Credentials de l'utilisateur dans la base de données qui a le rôle "applicationStorage".
	 * @param poNewCredentials Objet Credentials à sauvegarder.
	 * @returns Observable retournant l'objet Credentials enregistré.
	 */
	public persistCredentials(poNewCredentials: ICredentials): Observable<ICredentialsDocument> {
		return this.getUserCredentials()
			.pipe(
				mergeMap((poOldCredentials?: ICredentialsDocument) => {
					const loNewCredentialsDoc: ICredentialsDocument = poNewCredentials as ICredentialsDocument;
					loNewCredentialsDoc._id = SecurityService.C_APPLICATION_CREDENTIALS_STORAGE_DOC_ID;

					if (poOldCredentials) // On met à jour les champs que si c'est un doc qui existait déjà.
						loNewCredentialsDoc._rev = poOldCredentials._rev;

					if (this.areCredentialsDifferent(loNewCredentialsDoc, poOldCredentials)) {
						ConfigData.authentication.token = loNewCredentialsDoc.token;

						return this.isvcStore.putLocal(loNewCredentialsDoc, this.C_DATABASE_ID_APP_STORAGE)
							.pipe(
								mapTo(loNewCredentialsDoc),
								tap(

									_ => {
										if (!loNewCredentialsDoc.token && !loNewCredentialsDoc.tokenExpirationDate) {
											this.disconnect();
										}
										console.log(`${SecurityService.C_LOG_ID}${new Date().toLocaleString("fr-FR")} - Token: ${loNewCredentialsDoc.token} - Expiration: ${loNewCredentialsDoc.tokenExpirationDate}`)
									}
									,
									poError => console.error(`${SecurityService.C_LOG_ID}Erreur enregistrement nouveaux credentials :`, poError)
								)
							);
					}
					else {
						console.log(`${SecurityService.C_LOG_ID}Identifiants déjà à jour.`);
						return of(loNewCredentialsDoc);
					}
				})
			);
	}

	/** Supprime le token sauvegardé en local. */
	private clearToken(): Observable<ICredentialsDocument> {
		return this.getUserCredentials().pipe(mergeMap((poUserCredentials: ICredentialsDocument) => this.persistCredentials({ login: poUserCredentials.login })));
	}

	/** Permet de comparer les deux documents contenant les informations de connexion.
	 * @param poNewCredentials
	 * @param poOldCredentials
	 */
	private areCredentialsDifferent(poNewCredentials: ICredentialsDocument, poOldCredentials: ICredentialsDocument): boolean {
		return !poOldCredentials || poNewCredentials.login !== poOldCredentials.login || poNewCredentials.password !== poOldCredentials.password ||
			poNewCredentials.tokenExpirationDate !== poOldCredentials.tokenExpirationDate || poNewCredentials.token !== poOldCredentials.token;
	}

	/** Sauvegarde les paramètres de sécurité.
	 * @param poSecuritySettings Paramètres de sécurité à sauvegarder.
	 */
	public persistSecuritySettings(poSecuritySettings: ISecuritySettings): Observable<ISecuritySettingsDocument> {
		return this.getSavedSecuritySettings()
			.pipe(
				mergeMap((poOldSecuritySettingsDoc?: ISecuritySettingsDocument) => {
					const loNewSecuritySettingsDoc: ISecuritySettingsDocument = poSecuritySettings as ISecuritySettingsDocument;
					loNewSecuritySettingsDoc._id = SecurityService.C_APPLICATION_SECURITY_SETTINGS_STORAGE_DOC_ID;

					if (poOldSecuritySettingsDoc) // On met à jour la rev que si c'est un doc qui existait déjà.
						loNewSecuritySettingsDoc._rev = poOldSecuritySettingsDoc._rev;

					if (loNewSecuritySettingsDoc.unlockMode === undefined && poOldSecuritySettingsDoc)
						loNewSecuritySettingsDoc.unlockMode = poOldSecuritySettingsDoc.unlockMode;

					const loPersist$: Observable<IStoreDataResponse> = this.areSecuritySettingsDifferent(loNewSecuritySettingsDoc, poOldSecuritySettingsDoc) ?
						this.isvcStore.put(loNewSecuritySettingsDoc, this.C_DATABASE_ID_APP_STORAGE) : of(null);

					return loPersist$.pipe(mapTo(loNewSecuritySettingsDoc));
				})
			);
	}

	/** Permet de comparer les deux documents contenant les informations de connexion.
	 * @param poNewSecuritySettings
	 * @param poOldSecuritySettings
	 */
	private areSecuritySettingsDifferent(poNewSecuritySettings: ISecuritySettingsDocument, poOldSecuritySettings: ISecuritySettingsDocument): boolean {
		return !poOldSecuritySettings || poNewSecuritySettings.unlockMode !== poOldSecuritySettings.unlockMode;
	}

	/** Redirige vers la page d'authentification et retourne le sujet du service. */
	public requestAuthentication(psRedirectUrl?: string, poOptions?: IIndexedArray<any>): Promise<boolean> {
		const loNavigationOptions: NavigationOptions = { queryParams: {} };

		if (!StringHelper.isBlank(psRedirectUrl))
			loNavigationOptions.queryParams.redirectUrl = psRedirectUrl;

		ObjectHelper.assign(loNavigationOptions.queryParams, poOptions ?? {});

		return this.ioNavController.navigateRoot(ApplicationService.C_AUTHENTICATION_ROUTE_URL, loNavigationOptions);
	}

	/** Défini la méthode à appeler lorsque l'on touche l'écran. */
	public defineTouchHandler(): Observable<ICredentialsDocument> {
		this.isDomListening = true;

		return fromEvent(document.body, this.touchScreenEventName)
			.pipe(
				take(1),
				tap(_ => this.isDomListening = false),
				mergeMap(_ => {
					// Appel ver l'API permettant de prolonger la durée du token.
					return this.ioHttpClient.get<ISession>(this.securityApiPath, AuthenticatedRequestOptionBuilder.buildAuthenticatedRequestOptions())
						.pipe(mergeMap((poResult: ISession) => this.persistSession(poResult, ConfigData.authentication.credentials.login)));
				})
			);
	}

	/** Sauvergarde le token afin qu'il puisse être utilisé.
	 * @param psToken
	 */
	public persistSession(poSession: ISession, psLogin: string): Observable<ICredentialsDocument> {
		let loPersist$: Observable<ICredentialsDocument>;

		if (StringHelper.isBlank(psLogin))
			psLogin = ConfigData.authentication.credentials.login;

		if (UserData.current) {
			const loCredentials: ICredentials = {
				login: UserData.current.isGuest ? "" : psLogin,
				token: poSession.token,
				tokenExpirationDate: new Date(poSession.expireTimestamp),
				tokenCreationDate: new Date(poSession.createTimestamp)
			};

			loPersist$ = this.persistCredentials(loCredentials);
		}
		else
			loPersist$ = EMPTY;

		return loPersist$;
	}

	/** Permet de souscrire aux événements du securityService.
	 * @param pfNext Fonction appelée lors du next => function(poResult: ISecurityEvent).
	 * @param pfError Fonction appelée lors du error => function(poError: Any).
	 * @param pfComplete Fonction appelée lors du complete.
	 */
	public subscribe(pfNext: Function, pfError?: Function, pfComplete?: Function): void {
		this.moEventSubject.asObservable()
			.subscribe(
				(poResult: ISecurityEvent) => pfNext(poResult),
				poError => pfError(poError),
				() => pfComplete()
			);
	}

	/** Vérifie si l'utilisateur existe sur le serveur.
	 * @param psUserId
	 */
	public remoteUserExists(psUserId: string): Observable<boolean> {
		return this.ioHttpClient.get(`${this.securityApiPath}/users/${psUserId.toLowerCase()}/verify`, AuthenticatedRequestOptionBuilder.buildNonAuthenticatedRequestOptions())
			.pipe(
				catchError(poError => {
					console.warn(`${SecurityService.C_LOG_ID}Erreur vérification existence utilisateur sur le serveur :`, poError);
					return of(false);
				})
			) as Observable<boolean>;
	}

	/** Récupère le document utilisateur en local, peut être `undefined`.
	 * @param poUser Utilisateur dont il faut récupérer le document.
	 */
	public getLocalUserDocument(poUser: IUser): Observable<IUser>;
	/** Récupère le document utilisateur en local, peut être `undefined`.
	 * @param psLogin Login de l'utilisateur pour déterminer quel document utilisateur récupérer.
	 */
	public getLocalUserDocument(psLogin: string): Observable<IUser>;
	public getLocalUserDocument(poUserOrLogin: IUser | string): Observable<IUser> {
		const loDataSource: IDataSource = {
			databaseId: ArrayHelper.getFirstElement(this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.applicationStorage)),
			type: EStoreType.PouchDb,
			viewParams: { include_docs: true }
		};

		// Si on a un login en paramètre, il faut filtrer les possibles multi-comptes en filtrant le document de l'utilisateur qui tente de se connecter.
		if (typeof poUserOrLogin === "string") {
			// Le filtre permet de récupérer uniquement le(s) document(s) utilisateur(s) (y compris le document de contact utilisateur).
			loDataSource.filter = (poUser: IUser) => poUser.email === poUserOrLogin || poUser.name === poUserOrLogin;
		}
		else // Sinon, il suffit de récupérer le document de l'utilisateur via son identifiant.
			loDataSource.viewParams.key = poUserOrLogin._id;

		return this.isvcStore.get<IUser>(loDataSource)
			.pipe( // On retourne le premier élément des résultats qui comporte le préfixe `usr_` dans son identifiant.
				map((paResults: IUser[]) =>
					paResults.filter((poDocument: IUser) => IdHelper.hasPrefixId(poDocument._id) ? poDocument._id.includes(EPrefix.user) : true)
				),
				map((paFilteredResults: IUser[]) => {
					if (paFilteredResults.length > 1)
						console.warn(`${SecurityService.C_LOG_ID}Plusieurs documents éligibles pour le document utilisateur, conservation du premier qui n'a pas de préfixe couchbd par défaut`, paFilteredResults);

					const loUserDocWithoutCouchdbPrefix: IUser = paFilteredResults.find((poUser: IUser) => !poUser._id.includes(StoreDocumentHelper.C_COUCHDB_USER_PREFIX));

					return loUserDocWithoutCouchdbPrefix ? loUserDocWithoutCouchdbPrefix : ArrayHelper.getFirstElement(paFilteredResults) as IUser;
				})
			);
	}

	/** Ajoute une tâche au BTS pour vérifier le mode online et lancer l'envoi d'un heartbeat pour prolonger la session. */
	private raiseStartHeartbeatEvent(): void {
		this.raiseSecurityEvent({ type: EApplicationEventType.SecurityEvent, createDate: new Date(), data: { startHeartbeat: true } });
	}

	/** Permet de récupérer la config de l'authenticator en base.
	 * @param psAuthenticatorKey Clé de l'authenticator afin de le retrouver en base.
	 */
	public getAuthenticatorConfig(psAuthenticatorKey: string): Observable<IAuthenticatorParams> {
		const loParams: IDataSource = {
			databaseId: ArrayHelper.getFirstElement(this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.components)),
			viewName: "component/by_type",
			viewParams: {
				key: psAuthenticatorKey
			}
		};

		return this.isvcStore.get<IAuthenticatorParams>(loParams)
			.pipe(map((paResults: IAuthenticatorParams[]) => ArrayHelper.hasElements(paResults) ? ArrayHelper.getLastElement(paResults) : undefined));
	}

	/** Affichage d'une popup lorsqu'une erreur est survenue lors de l'authentification de l'utilisateur (login ou mdp incorrect).
	 * @param poError Erreur http ou texte qui indique l'erreur survenue.
	 */
	private authenticatePopupError(poError: HttpErrorResponse, psEmail: string): void {
		const loError: HttpErrorResponse | string = poError || "bad login or password";
		const loPopupParams = new ShowMessageParamsPopup({ header: "Erreur" });

		switch (poError.status) {
			case 500:
				loPopupParams.message = `Une erreur inattendue est survenue sur le serveur. Veuillez contacter le support <a href='${ConfigData.appInfo.supportUrl}' target='_blank'>ICI</a>.`;
				break;

			case 0:
				loPopupParams.message = "Veuillez vérifier votre connexion internet.";
				break;

			case 401:
			case 404:
				let lsMessage: string;
				if ((poError.error?.message).includes(ESignupErrorCode.ACCOUNT_BLOCKED))
					lsMessage = ConfigData.environment.accountBlockedMessage ?? "Compte bloqué.";
				else if (poError.error?.message.includes("Le compte n\'existe pas ou est désactivé."))
					lsMessage = poError.error?.message;
				else
					lsMessage = "Identifiant ou mot de passe invalide.";

				loPopupParams.header = "Erreur d'authentification";
				loPopupParams.message = lsMessage;
				break;

			case 403:
				if (poError.error.code === ESignupErrorCode.EMAIL_NOT_VERIFIED) {
					loPopupParams.message = `Votre adresse email n'a pas encore été vérifiée. Si vous n'avez pas reçu le mail de vérification, `;
					loPopupParams.buttons.push({
						text: "Renvoyer le mail",
						handler: () => { this.ioNavController.navigateForward(["signup", "send-validation-email"], { queryParams: { email: psEmail } }); }
					} as AlertButton);
				}
				break;

			default:
				loPopupParams.header = `Erreur ${poError.status}`;
				loPopupParams.message = `Une erreur inattendue est survenue sur le serveur. Veuillez contacter le support <a href='${ConfigData.appInfo.supportUrl}' target='_blank'>ICI</a>.`;
				break;
		}

		this.isvcUiMessage.showMessage(loPopupParams);
		console.error(`${SecurityService.C_LOG_ID}Erreur lors de l'authentification de l'utilisateur :`, loError);
	}

	/** Affichage d'une popup lorsqu'une erreur est survenue lors de l'authentification de l'utilisateur (login ou mdp incorrect).
	 * @param poError Erreur http ou texte qui indique l'erreur survenue.
	 */
	private authenticateShowErrorAnakin(poError: HttpErrorResponse, psEmail: string): void {
		const loError: HttpErrorResponse | string = poError || "bad login or password";
		let messageErreur: string = "";

		switch (poError.status) {
			case 500:
				messageErreur = `Une erreur inattendue est survenue sur le serveur. Veuillez contacter le support <a href='${ConfigData.appInfo.supportUrl}' target='_blank'>ICI</a>`;
				break;

			case 0:
				messageErreur = "Veuillez vérifier votre connexion internet";
				break;

			case 401:
			case 404:
				let lsMessage: string;
				if ((poError.error?.message).includes(ESignupErrorCode.ACCOUNT_BLOCKED))
					lsMessage = ConfigData.environment.accountBlockedMessage ?? "Compte bloqué";
				else if (poError.error?.message.includes("Le compte n\'existe pas ou est désactivé"))
					lsMessage = poError.error?.message;
				else
					lsMessage = "Identifiant ou mot de passe invalide";


				messageErreur = lsMessage;
				break;

			case 403:
				if (poError.error.code === ESignupErrorCode.EMAIL_NOT_VERIFIED) {
					messageErreur = `Votre adresse email n'a pas encore été vérifiée`;
				}
				break;

			default:
				messageErreur = `Une erreur inattendue est survenue sur le serveur. Veuillez contacter le support <a href='${ConfigData.appInfo.supportUrl}' target='_blank'>ICI</a>`;
				break;
		}

		console.error(`${SecurityService.C_LOG_ID}Erreur lors de l'authentification de l'utilisateur :`, loError);
		this.svcError.setErrorConnexion(messageErreur);
	}

	/** Affichage d'une popup lorsque une erreur est survenue lors de l'initialisation de l'application après authentification.
	 * @param poError Erreur survenue.
	 * @param psErrorType Indique le type d'erreur survenue.
	 */
	private innerAuthenticateAndSaveUserPopupError(poError: any, psErrorType: string): void {
		this.isvcUiMessage.showMessage(
			new ShowMessageParamsPopup({
				message: `Une erreur est survenue lors de ${psErrorType} de votre document utilisateur. Veuillez réessayer ultérieurement.`,
				header: "Erreur"
			})
		);

		console.error(`${SecurityService.C_LOG_ID}Échec de ${psErrorType} du document utilisateur :`, poError);
	}

	/** Affichage d'une erreur lors de l'initialisation de l'application après authentification.
	 * @param poError Erreur survenue.
	 * @param psErrorType Indique le type d'erreur survenue.
	 */
	private innerAuthenticateAndSaveUserErrorAnakin(poError: any, psErrorType: string): Observable<never> {
		console.error(`${SecurityService.C_LOG_ID}Échec de ${psErrorType} du document utilisateur :`, poError);
		const messageError = `Une erreur est survenue lors de ${psErrorType} de votre document utilisateur. Veuillez réessayer ultérieurement.`;
		this.svcError.setErrorConnexion(messageError);
		return throwError(messageError);
	}

	/** Retourne une erreur survenue lors de l'authentification de l'utilisateur (mauvaise réponse serveur) et affiche une popup. */
	private raiseInnerAuthenticateAndSaveUserPopupError(): Observable<never> {
		const lsErrorMessage = "Le token n'a pas été trouvé dans la session.";

		this.isvcUiMessage.showMessage(
			new ShowMessageParamsPopup({ header: "Erreur serveur", message: "Le serveur n'a pas renvoyé de réponse. Veuillez réessayer ultérieurement." })
		);

		console.error(`${SecurityService.C_LOG_ID}Erreur lors de la tentative d'initialisation lors de l'authentification :`, lsErrorMessage);
		return throwError(lsErrorMessage);
	}

	/** Retourne une erreur survenue lors de l'authentification de l'utilisateur (mauvaise réponse serveur). */
	private raiseInnerAuthenticateAndSaveUserErrorAnakin(): Observable<never> {
		const lsErrorMessage = "Le token n'a pas été trouvé dans la session.";
		console.error(`${SecurityService.C_LOG_ID}Erreur lors de la tentative d'initialisation lors de l'authentification :`, lsErrorMessage);
		return throwError(lsErrorMessage);
	}

	/** Un utilisateur correspond au login entré, on tente de se connecter avec le mot de passe fourni.
	 * @param poNewUser Document utilisateur de son compte.
	 * @param psLogin Login de l'utilisateur.
	 * @param psPassword Mot de passe de l'utilisateur.
	 */
	private authenticateAndSaveUser(psLogin: string, psPassword: string): Observable<IUser> {
		return this.authenticate(psLogin, psPassword)
			.pipe(
				tap(_ => { }, poError => this.authenticatePopupError(poError, psLogin)),
				mergeMap((poResult?: IUser) => !poResult ? throwError(null) : of(poResult)),
				mergeMap((poResult: IUser) => {
					return of(true)
						.pipe(
							mergeMap(_ => {
								if (this.isvcPin.isValid(psPassword) && this.isvcPlatform.isMobileApp)
									return this.isvcPin.save(psLogin, psPassword);
								else
									return of(undefined);
							}),
							mapTo(poResult)
						);
				}),
				tap((poResult: IUser) => {
					poResult._id = StoreDocumentHelper.removeUserCouchDBPrefix(poResult);

					ConfigData.appInfo.firstName = StringHelper.isBlank(poResult.firstName) ? "" : ContactHelper.getFormattedFirstName(poResult.firstName);
					ConfigData.appInfo.lastName = StringHelper.isBlank(poResult.lastName) ? "" : ContactHelper.getFormattedLastName(poResult.lastName);
					ConfigData.appInfo.login = psLogin;
					ConfigData.authentication.credentials.login = ConfigData.appInfo.login;
					ConfigData.appInfo.versionAppErgo = "2";
					localStorage.setItem("versionAppErgo", "2");
				}),
				mergeMap((poResult: IUser) => this.innerAuthenticateAndSaveUser(poResult)),
				mergeMap((poResult: IUser) => this.persistSession(poResult.session, psLogin).pipe(mapTo(poResult))),
			);
	}

	/** Un utilisateur correspond au login entré, on tente de se connecter avec le mot de passe fourni.
 * @param poNewUser Document utilisateur de son compte.
 * @param psLogin Login de l'utilisateur.
 * @param psPassword Mot de passe de l'utilisateur.
 */
	private authenticateAndSaveUserAnakin(psLogin: string, psPassword: string): Observable<IUser> {
		return this.authenticate(psLogin, psPassword)
			.pipe(
				tap(_ => { }, poError => throwError(this.authenticateShowErrorAnakin(poError, psLogin))),
				mergeMap((poResult?: IUser) => !poResult ? throwError(null) : of(poResult)),
				mergeMap((poResult: IUser) => {
					if (this.isvcPin.isValid(psPassword) && this.isvcPlatform.isMobileApp) {
						return from(this.isvcPin.save(psLogin, psPassword)).pipe(
							mapTo(poResult)
						);
					} else {
						return of(poResult);
					}
				}),
				tap((poResult: IUser) => {
					poResult._id = StoreDocumentHelper.removeUserCouchDBPrefix(poResult);

					ConfigData.appInfo.firstName = StringHelper.isBlank(poResult.firstName) ? "" : ContactHelper.getFormattedFirstName(poResult.firstName);
					ConfigData.appInfo.lastName = StringHelper.isBlank(poResult.lastName) ? "" : ContactHelper.getFormattedLastName(poResult.lastName);
					ConfigData.appInfo.login = psLogin;
					ConfigData.authentication.credentials.login = ConfigData.appInfo.login;
					ConfigData.appInfo.versionAppErgo = "2";
					localStorage.setItem("versionAppErgo", "2");
				}),
				mergeMap((poResult: IUser) => this.innerAuthenticateAndSaveUserAnakin(poResult)),
				mergeMap((poResult: IUser) => this.persistSession(poResult.session, psLogin).pipe(mapTo(poResult)))
			);
	}

	/** Récupère un nouveau token pour l'utilisateur sur le serveur.
	 * @param psLogin
	 * @param psPassword
	 */
	private authenticate(psLogin: string, psPassword: string): Observable<IUser> {
		const loRequestOptions: IHttpOptions<"json"> = {
			responseType: "json",
			headers: new HttpHeaders({ appInfo: OsappApiHelper.stringifyForHeaders(ConfigData.appInfo), Authorization: btoa(`${psLogin}:${psPassword}`) })
		};

		return this.ioHttpClient.get(`${this.securityApiPath}/users/authenticate`, loRequestOptions) as Observable<IUser>;
	}

	private innerAuthenticateAndSaveUser(poNewUser: IUser): Observable<IUser> {
		if (poNewUser.session && !StringHelper.isBlank(poNewUser.session.token)) {
			return this.getLocalUserDocument(poNewUser)
				.pipe(
					tap(_ => { }, poError => this.innerAuthenticateAndSaveUserPopupError(poError, "la récupération")),
					mergeMap((poOldUser: IUser) => {
						UserData.current = poNewUser;

						if (poNewUser.isGuest)
							return of(poNewUser);
						else {
							return this.saveUserDocument(poNewUser, poOldUser)
								.pipe(tap(_ => { }, poError => this.innerAuthenticateAndSaveUserPopupError(poError, "l'enregistrement")));
						}
					}),
					mapTo(poNewUser)
				);
		}
		else
			return this.raiseInnerAuthenticateAndSaveUserPopupError();
	}

	private innerAuthenticateAndSaveUserAnakin(poNewUser: IUser): Observable<IUser> {
		if (poNewUser.session && !StringHelper.isBlank(poNewUser.session.token)) {
			return this.getLocalUserDocument(poNewUser)
				.pipe(
					tap(_ => { }, poError => this.innerAuthenticateAndSaveUserErrorAnakin(poError, "la récupération")),
					mergeMap((poOldUser: IUser) => {
						UserData.current = poNewUser;

						if (poNewUser.isGuest)
							return of(poNewUser);
						else {
							return this.saveUserDocument(poNewUser, poOldUser)
								.pipe(tap(_ => { }, poError => this.innerAuthenticateAndSaveUserErrorAnakin(poError, "l'enregistrement")));
						}
					}),
					mapTo(poNewUser)
				);
		}
		else
			return this.raiseInnerAuthenticateAndSaveUserErrorAnakin();
	}

	/** Recherche le token avec les login/mdp.
	 * @param psLogin Login de l'utilisateur.
	 * @param psPassword Mot de passe de l'utilisateur.
	 * @return Retourne le token de session en mode Online, ou null en mode Offline
	 */
	public authenticateUser(psLogin: string, psPassword: string): Observable<IUser> {
		return this.isvcNetwork.asyncIsNetworkReliable()
			.pipe(
				mergeMap((pbHasNetwork: boolean) => {
					let loAuthentication$: Observable<IUser>;

					if (pbHasNetwork)
						loAuthentication$ = this.authenticateAndSaveUser(psLogin, psPassword);
					else {
						this.isvcUiMessage.showMessage(new ShowMessageParamsPopup({ message: "Une connexion internet est requise pour l'authentification.", header: "Erreur" }));
						loAuthentication$ = throwError("Internet connection is needed for login/password authentication.");
					}

					return loAuthentication$
						.pipe(
							mergeMap((poUser: IUser) => this.getUserCredentials().pipe(tap((poCredentials: Credentials) => this.setUserConfigs(poUser, poCredentials)))),
							mergeMap((poCredentials: Credentials) => {
								if (ConfigData.authentication.token !== poCredentials.token) {
									console.log(`${SecurityService.C_LOG_ID}Votre nouveau token : ${poCredentials.token}`);
									ConfigData.authentication.token = poCredentials.token;
								}

								return poCredentials.isValidToken() ? of(null) : this.raiseSessionExpiredPopupError();
							}),
							tap(
								_ => {
									this.setAuthenticationStatus(true);
									const loSafeUserData: IUser = { ...UserData.current };
									delete loSafeUserData.session.token;
									this.isvcLogger.action(SecurityService.C_LOG_ID, "User logged in successfully with login/password.", ELogActionId.login, { user: loSafeUserData }, undefined, true);
								},
								poError => this.logAuthenticationActionFailed("User authentication failed", poError)
							),
							mergeMap(_ => this.updateUnlockMode(EUnlockMode.loginPassword)),
							mapTo(UserData.current),
							tap(_ => { }, _ => this.setAuthenticationStatus(false))
						);
				})
			);
	}

	/** Recherche le token avec les login/mdp.
	 * @param psLogin Login de l'utilisateur.
	 * @param psPassword Mot de passe de l'utilisateur.
	 * @return Retourne le token de session en mode Online, ou null en mode Offline
	 */
	public authenticateUserAnakin(psLogin: string, psPassword: string): Observable<IUser> {
		return this.isvcNetwork.asyncIsNetworkReliable()
			.pipe(
				mergeMap((pbHasNetwork: boolean) => {
					let loAuthentication$: Observable<IUser>;

					if (pbHasNetwork)
						loAuthentication$ = this.authenticateAndSaveUserAnakin(psLogin, psPassword);
					else {
						this.svcError.setErrorConnexion("Une connexion internet est requise pour l'authentification.");
						loAuthentication$ = throwError("Internet connection is needed for login/password authentication.");
					}

					return loAuthentication$
						.pipe(
							mergeMap((poUser: IUser) => this.getUserCredentials().pipe(tap((poCredentials: Credentials) => this.setUserConfigs(poUser, poCredentials)))),
							mergeMap((poCredentials: Credentials) => {
								if (ConfigData.authentication.token !== poCredentials.token) {
									console.log(`${SecurityService.C_LOG_ID}Votre nouveau token : ${poCredentials.token}`);
									ConfigData.authentication.token = poCredentials.token;
								}

								return poCredentials.isValidToken() ? of(null) : this.raiseSessionExpiredError();
							}),
							tap(
								_ => {
									const loSafeUserData: IUser = { ...UserData.current };
									delete loSafeUserData.session.token;
									this.isvcLogger.action(SecurityService.C_LOG_ID, "User logged in successfully with login/password.", ELogActionId.login, { user: loSafeUserData }, undefined, true);
									if (!this.isvcPlatform.isMobileApp) {
										//On charge le script que si on est pas sur mobile app car il faut les SDK mobile
										this.svcSurvicate.init();
									}
								},
								poError => this.logAuthenticationActionFailed("User authentication failed", poError)
							),
							mergeMap(_ => this.updateUnlockMode(EUnlockMode.loginPassword)),
							mapTo(UserData.current),
							tap(_ => { }, _ => this.setAuthenticationStatus(false))
						);
				})
			);
	}

	public setAuthenticationStatus(pbIsAuthenticated: boolean): void {
		if (!pbIsAuthenticated)
			UserData.current = undefined;
		this.moAuthStatusSubject.next({ isAuthenticated: pbIsAuthenticated, isGuest: UserData.current && UserData.current.isGuest } as IAuthStatus);
		this.moAuthenticatedSubject.next(pbIsAuthenticated);
	}

	private raiseSessionExpiredPopupError(): Observable<never> {
		this.isvcUiMessage.showMessage(
			new ShowMessageParamsPopup({ message: "Veuillez vous connecter à internet pour recharger votre session.", header: "Session expirée" })
		);

		return throwError(new OsappError("La session de l'utilisateur a expirée, connexion avec internet requise."));
	}

	private raiseSessionExpiredError(): Observable<never> {
		const messageError = "La session de l'utilisateur a expirée, connexion avec internet requise";
		this.svcError.setErrorConnexion(messageError)
		return throwError(new OsappError(messageError));
	}

	/** Enregistre le document utilisateur de l'utilisateur.
	 * @param poNewUser Document utilisateur de l'utilisateur.
	 * @param poOldUser Utilisateur stocké en base locale.
	 */
	public saveUserDocument(poNewUser: IUser, poOldUser: IUser): Observable<IStoreDataResponse> {
		if (poOldUser)
			poNewUser._rev = poOldUser._rev;

		return this.isvcStore.put(poNewUser, ArrayHelper.getFirstElement(this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.applicationStorage)));
	}

	/** Défini les données de configuration liées à l'utilisateur.
	 * @param poUser Document utilisateur.
	 * @param poCredentials Données de connexion de l'utilisateur.
	 */
	private setUserConfigs(poUser: IUser, poCredentials: ICredentialsDocument): void {
		poUser.workspaceInfos = ArrayHelper.hasElements(poUser.workspaceInfos) ?
			poUser.workspaceInfos.filter((poWorkspaceInfo: IWorkspaceInfo) => poWorkspaceInfo.appId === ConfigData.appInfo.appId) : [];

		UserData.current = poUser;
		ConfigData.authentication.userId = this.msUserId = poUser.name;
		ConfigData.authentication.credentials.login = poCredentials.login;
	}

	/** Affiche la connexion par empreinte digitale. */
	public showFingerprintScanner(): Observable<boolean> {
		//! Le plugin a une doc moisie, l'interface utilisée n'a pas de 'clientId' ni de 'clientSecret' mais ces paramètres sont nécessaires pour Android 8-,
		//! on caste donc en `any`.
		return from(this.ioFingerprintAIO.show({
			title: `Déverrouillage pour ${ConfigData.appInfo.appName}.`,
			disableBackup: true,
			description: "Authentification biométrique pour accéder à votre compte.",
			cancelButtonTitle: "Annuler",
			clientId: "fingerprint",
			clientSecret: "password"
		} as any))
			.pipe(
				mapTo(true),
				catchError(poError => {
					if (poError.code !== this.ioFingerprintAIO.BIOMETRIC_DISMISSED) {	// On affiche rien en cas d'annulation volontaire par l'utilisateur.
						this.isvcUiMessage.showMessage(new ShowMessageParamsPopup({ message: "Erreur d'authentification", header: "Erreur" }));
						return throwError(poError);
					}
					else
						return of(false);
				})
			);
	}

	/** Déverrouille l'application.
	 * ### ATTENTION : Le token doit être défini !
	 * @param poCredentials Document contenant les informations d'authentification de l'utilisateur.
	 */
	public unlockApp(poCredentials: ICredentialsDocument): Observable<boolean> {
		return this.getLocalUserDocument(poCredentials.login)
			.pipe(
				mergeMap((poUser?: IUser) => this.innerUnlockApp(poCredentials, poUser)),
				tap(
					() => {
						this.setAuthenticationStatus(true);
						this.isvcLogger.action(SecurityService.C_LOG_ID, "User unlocked the application.", ELogActionId.login, UserData.current);
					},
					poError => this.logAuthenticationActionFailed("User unlock app failed", poError)
				),
				mapTo(true),
				catchError(poError => {
					this.setAuthenticationStatus(false);
					console.error(`${SecurityService.C_LOG_ID}Erreur déverrouillage de l'app :`, poError);
					poCredentials.tokenExpirationDate = undefined;
					return this.persistCredentials(poCredentials).pipe(mergeMapTo(throwError(poError)));
				})
			);
	}

	private innerUnlockApp(poCredentials: ICredentialsDocument, poUser?: IUser): Observable<undefined | never> {
		if (poUser) {
			this.setUserConfigs(poUser, poCredentials);
			ConfigData.authentication.token = poCredentials.token;
			ConfigData.appInfo.login = poCredentials.login;
			return of(undefined);
		}
		else {
			this.isvcUiMessage.showMessage(
				new ShowMessageParamsPopup({
					message: "Une erreur est survenue lors de la récupération de votre profil.\nVeuillez vous reconnecter par mot de passe.",
					header: "Erreur"
				})
			);

			return throwError(`Erreur critique : pas de document utilisateur pour '${poCredentials.login}'.`);
		}
	}

	/** Permet de mettre à jour le mode de déverrouillage.
	 * @param peLockMode Mode de déverrouillage de l'application.
	 */
	public updateUnlockMode(peLockMode: EUnlockMode): Observable<ISecuritySettingsDocument> {
		return this.persistSecuritySettings({ unlockMode: peLockMode });
	}

	/** Déverrouille l'application à l'aide de l'empreinte digitale. */
	public unlockFingerprint(): Observable<ISecuritySettingsDocument> {
		return this.getUserCredentials()
			.pipe(
				mergeMap((poResultCredentials: ICredentialsDocument) => {
					const lsUnlockFailedMessage = "User unlock app failed";

					return this.showFingerprintScanner()
						.pipe(
							tap(
								(pbResult: boolean) => {
									if (!pbResult)
										this.logAuthenticationActionFailed(lsUnlockFailedMessage);
								},
								poError => this.logAuthenticationActionFailed(lsUnlockFailedMessage, poError)
							),
							filter((pbResult: boolean) => pbResult),
							mergeMap(_ => this.unlockApp(poResultCredentials))
						);
				}),
				mergeMap(_ => this.updateUnlockMode(EUnlockMode.fingerprint))
			);
	}

	/** Déverrouille l'application à l'aide du code pin.
	 * @param psPin le code PIN entré par l'utilisateur
	 * @returns Observable de ISecuritySettingsDocument
	*/
	public unlockPin(psPin: string): Observable<ISecuritySettingsDocument> {
		if (!this.isvcPlatform.isMobileApp) {
			throw new Error("Déverrouillage par PIN supporté uniquement sur application mobile");
		}
		return this.getUserCredentials()
			.pipe(
				mergeMap((poResultCredentials: ICredentialsDocument) => {
					return from(this.isvcPin.check(poResultCredentials.login, psPin)).pipe(
						mergeMap((pbIsCorrectPin: boolean) => {
							if (pbIsCorrectPin)
								return this.unlockApp(poResultCredentials);
							else {
								this.logAuthenticationActionFailed("User unlock app failed");
								return throwError(new Error("Erreur lors de la vérification du code pin."));
							}
						})
					);
				}),
				mergeMap(_ => this.updateUnlockMode(EUnlockMode.pin))
			);
	}


	/** Déconnecte l'utilisateur courant et navigue vers la page de connexion. */
	public disconnect(): Observable<boolean> {
		return this.clearToken()
			.pipe(
				tap(_ => {
					this.setAuthenticationStatus(false);
					// Log qui sera téléverser sur la base des logs lors de la prochaine connexion car plus les droits pour écrire sur la base.
					this.isvcLogger.action(SecurityService.C_LOG_ID, "User logged out.", ELogActionId.logout, undefined, true);
				}),
				mergeMap(_ => this.requestAuthentication())
			);
	}

	private logAuthenticationActionFailed(psMessage: string, poError?: any): void {
		this.isvcLogger.action(SecurityService.C_LOG_ID, psMessage, ELogActionId.authenticationFailed, poError);
	}

	//#endregion

}
